import fetch from '../utils/fetch';
import humps from 'humps';
import NetworkError from '../utils/error/NetworkError';

import {AUTH_LOG_OUT} from './auth';

const API_ORIGIN = process.env.PUBLIC_URL;
const API_ENDPOINT = process.env.API_ENDPOINT;
const API_PAGINATION_MAX_LIMIT = 100;

const isBlob = typeof Blob === 'function'
  ? value => value instanceof Blob
  : () => false;

function asUrlEncoded(object) {
  return Object.keys(object)
    .filter(k => object[k] !== undefined)
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(object[k])}`)
    .join('&');
}

function flattenObject(value, into = {}, currentPath = '') {
  if (Array.isArray(value)) {
    value.forEach((item, idx) => flattenObject(
      item,
      into,
      `${currentPath}[${idx}]`
    ));
  } else if (Object(value) === value && !isBlob(value)) {
    Object.keys(value).forEach(key => flattenObject(
      value[key],
      into,
      currentPath ? `${currentPath}[${key}]` : key
    ));
  } else if (value !== undefined) {
    into[currentPath] = value;
  }

  return into;
}

export function request(path, {qs = {}, body = undefined, headers = {}, raw, ...options}) {
  return (dispatch, getState) => {
    const state = getState();
    const {activeChannel, activeLocale} = state.channels;
    const {token} = state.auth;
    let url = [API_ENDPOINT, activeChannel, activeLocale, path].join('/');
    const flatQs = flattenObject(qs);

    if (Object.keys(flatQs).length) {
      url += '?' + asUrlEncoded(flatQs);
    }

    if (Object(body) === body) {
      const flat = flattenObject(body);
      if (Object.values(flat).some(isBlob)) {
        body = new FormData();
        Object.keys(flat).forEach(key => body.append(key, flat[key]));
      } else {
        body = asUrlEncoded(flat);
        headers['Content-Type'] = 'application/x-www-form-urlencoded';
      }
    }

    headers['Accept'] = 'application/json';

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // if (process.env.SERVER) {
      headers['Origin'] = API_ORIGIN;
      headers['X-Origin'] = API_ORIGIN;
    // }

  const makeRequest = () => {
    return fetch(encodeURI(url), {headers, body, ...options})
      .then((response) => {
        const contentType = response.headers.get('content-type');
        return Promise.all([
          response,
          //(contentType && contentType.includes('application/json')) ? response.json() : {}
          // FIXME: Correct the API responses, then remove this hack
          response.text().then(text => {
            try {
              return JSON.parse(text);
            } catch (e) {
              return {};
            }
          })
        ]);
      })
      .then(([response, json]) => {
        if (!raw) {
          json = humps.camelizeKeys(json);
        }
        if (response.ok) {
          const {embedded, links, ...rest} = json;
          return embedded ? {content: embedded, meta: {links, ...rest}} : {content: json};
        } else if (response.status === 409) {
          return new Promise(resolve => setTimeout(() => resolve(makeRequest()), 1000));
        }
        throw new NetworkError(response.status, json, {
          method: options.method || 'GET',
          headers,
          url,
          body
        });
      })
      .catch(err => {
        if (
          err.meta &&
          err.meta.url &&
          err.meta.url.includes('.well-known') &&
          err.status === 403
        ) {
          throw new NetworkError(404, {code: 404, message: 'Not found'});
        }

        if (err.status === 403) {
          dispatch({type: AUTH_LOG_OUT});
        }
        throw err;
      });
  };
    return makeRequest();
  };
}

export default {
  get(path, qs, options = null) {
    return request(path, {...options, qs});
  },

  getAll(path, qs, options = null) {
    return dispatch => dispatch(this.get(path, {...qs, page: 1, limit: API_PAGINATION_MAX_LIMIT}, options))
      .then(apiResponse => {
        if (apiResponse.meta.pages === 1) {
          return apiResponse;
        }

        const requestActions = (new Array(apiResponse.meta.pages - 1))
          .fill()
          .map((_, idx) => this.get(path, {...qs, page: idx + 2, limit: API_PAGINATION_MAX_LIMIT}, options));

        return Promise.all(requestActions.map(dispatch))
          .then(responses => ({
            ...apiResponse,
            content: {
              ...apiResponse.content,
              items: apiResponse.content.items.concat(...responses.map(it => it.content.items))
            }
          }));
      });
  },

  post(path, body, qs, options = null) {
    return request(path, {
      ...options,
      method: 'POST',
      body,
      qs,
    });
  },

  put(path, body, qs, options = null) {
    return request(path, {
      ...options,
      method: 'PUT',
      body,
      qs
    });
  },

  delete(path, body, qs, options = null) {
    return request(path, {
      ...options,
      method: 'DELETE',
      body,
      qs
    });
  }
}



// WEBPACK FOOTER //
// ./ducks/api.js